import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import Modal from '../components/Modal';
import TrainerConfigForm, { isTrainerConfigReady, useTrainerKindMeta } from '../components/TrainerConfigForm';
import { useAuth } from '../context/AuthContext';
import { saveGuestTrainerSession } from '../utils/guestPractice';

const GROUP_OPTIONS = [
  { value: 'all', label: 'Все' },
  { value: 'basics', label: 'Основы' },
  { value: 'ege', label: 'ЕГЭ' },
  { value: 'regex', label: 'Регулярки' },
  { value: 'excel', label: 'Excel' },
];

const GROUP_LABELS = {
  basics: 'Основы',
  ege: 'ЕГЭ',
  regex: 'Регулярки',
  excel: 'Excel',
};

const GROUP_ORDER = ['basics', 'ege', 'regex', 'excel'];

function pluralTrainers(n) {
  if (n === 1) return 'тренажёр';
  if (n >= 2 && n <= 4) return 'тренажёра';
  return 'тренажёров';
}

function egeSectionLabel(examKey) {
  if (!examKey) return 'ЕГЭ';
  return `ЕГЭ-${examKey.replace('-', '–')}`;
}

function TrainerCard({ item, onSetup }) {
  return (
    <li className="practice-quizzes-item">
      <div className="practice-quizzes-item-main">
        <h2 className="practice-quizzes-item-title">{item.title}</h2>
        <p className="practice-quizzes-item-meta text-secondary">{item.blurb}</p>
      </div>
      <div className="practice-quizzes-item-actions">
        {item.implemented ? (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => onSetup(item.slug)}
          >
            Настроить
          </button>
        ) : (
          <span
            className="btn btn-outline btn-sm"
            style={{ pointerEvents: 'none', opacity: 0.7 }}
          >
            Скоро
          </span>
        )}
      </div>
    </li>
  );
}

function FlatTrainerList({ items, onSetup }) {
  if (!items.length) {
    return <p className="text-secondary">В этом разделе пока нет тренажёров.</p>;
  }
  return (
    <ul className="practice-quizzes-list">
      {items.map((item) => (
        <TrainerCard key={item.slug} item={item} onSetup={onSetup} />
      ))}
    </ul>
  );
}

function EgeAccordion({ items, onSetup }) {
  const buckets = useMemo(() => {
    const map = new Map();
    for (const item of items) {
      const key = item.exam_key || 'other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    return Array.from(map.entries());
  }, [items]);

  const [expanded, setExpanded] = useState(() => new Set());

  if (!items.length) {
    return <p className="text-secondary">В этом разделе пока нет тренажёров.</p>;
  }

  function toggle(key) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <ul className="practice-quizzes-ege-tree">
      {buckets.map(([examKey, bucketItems]) => {
        const open = expanded.has(examKey);
        return (
          <li key={examKey} className="practice-quizzes-ege-node">
            <button
              type="button"
              className="practice-quizzes-ege-toggle"
              aria-expanded={open}
              onClick={() => toggle(examKey)}
            >
              <span className="practice-quizzes-ege-toggle-label">
                {egeSectionLabel(examKey)}
              </span>
              <span className="practice-quizzes-ege-toggle-meta text-secondary">
                {bucketItems.length} {pluralTrainers(bucketItems.length)}
              </span>
              <span className="practice-quizzes-ege-chevron" aria-hidden>
                {open ? '▾' : '▸'}
              </span>
            </button>
            {open ? (
              <ul className="practice-quizzes-list practice-quizzes-ege-children">
                {bucketItems.map((item) => (
                  <TrainerCard key={item.slug} item={item} onSetup={onSetup} />
                ))}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Каталог тренажёров: свободный старт, выданные назначения, история.
 */
export default function TrainersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const groupParam = searchParams.get('group') || 'all';
  const activeGroup = GROUP_OPTIONS.some((o) => o.value === groupParam) ? groupParam : 'all';

  const [items, setItems] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [setupSlug, setSetupSlug] = useState(null);
  const [starting, setStarting] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(() => new Set(GROUP_ORDER));

  const { meta, loading: metaLoading, error: metaError, defaultConfig } =
    useTrainerKindMeta(setupSlug);
  const [config, setConfig] = useState({});

  useEffect(() => {
    if (defaultConfig && Object.keys(defaultConfig).length) {
      setConfig({ ...defaultConfig });
    }
  }, [defaultConfig]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catalogRes, assignRes, sessRes] = await Promise.all([
          api.get('/trainers/catalog/'),
          user
            ? api.get('/trainers/assignments/').catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
          user
            ? api.get('/trainers/sessions/').catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
        ]);
        if (!cancelled) {
          setItems(Array.isArray(catalogRes.data) ? catalogRes.data : []);
          setAssignments(Array.isArray(assignRes.data) ? assignRes.data : []);
          setSessions(Array.isArray(sessRes.data) ? sessRes.data : []);
        }
      } catch {
        if (!cancelled) setError('Не удалось загрузить тренажёры.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const byGroup = useMemo(() => {
    const result = { basics: [], ege: [], regex: [], excel: [] };
    for (const item of items) {
      const g = result[item.group] ? item.group : 'basics';
      result[g].push(item);
    }
    return result;
  }, [items]);

  function setGroup(value) {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') next.delete('group');
    else next.set('group', value);
    setSearchParams(next, { replace: true });
  }

  function toggleSection(group) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }

  function openSetup(slug) {
    setError('');
    setSetupSlug(slug);
  }

  async function startFree() {
    if (!setupSlug) return;
    setStarting(true);
    setError('');
    try {
      if (user) {
        const res = await api.post('/trainers/sessions/start/', {
          kind: setupSlug,
          config,
        });
        navigate(`/trainers/sessions/${res.data.id}`);
        return;
      }
      const res = await api.post('/trainers/guest/start/', {
        kind: setupSlug,
        config,
      });
      saveGuestTrainerSession(res.data);
      navigate('/trainers/sessions/guest');
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось начать.');
    } finally {
      setStarting(false);
    }
  }

  const setupItem = items.find((item) => item.slug === setupSlug);

  function closeSetup() {
    if (starting) return;
    setSetupSlug(null);
    setError('');
  }

  async function startAssignment(assignmentId) {
    setStarting(true);
    setError('');
    try {
      const res = await api.post(`/trainers/assignments/${assignmentId}/start/`);
      navigate(`/trainers/sessions/${res.data.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось начать.');
    } finally {
      setStarting(false);
    }
  }

  function renderGroupBody(group) {
    const groupItems = byGroup[group] || [];
    if (group === 'ege') {
      return <EgeAccordion items={groupItems} onSetup={openSetup} />;
    }
    return <FlatTrainerList items={groupItems} onSetup={openSetup} />;
  }

  return (
    <div className="practice-quizzes-page">
      <div className="catalog-page-header">
        <h1 className="catalog-page-title">Тренажёры</h1>
        <p className="catalog-page-lead">
          Короткие задания с генератором — отдельно от домашнего задания и тестов.
          {!user ? ' Результаты не сохраняются — войдите, чтобы вести историю.' : ''}
        </p>
      </div>

      <div className="konspekty-scope-bar practice-quizzes-group-bar" role="group" aria-label="Разделы тренажёров">
        {GROUP_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            className={activeGroup === o.value ? 'konspekty-scope-btn active' : 'konspekty-scope-btn'}
            onClick={() => setGroup(o.value)}
          >
            {o.label}
            {o.value !== 'all' && byGroup[o.value]?.length
              ? ` (${byGroup[o.value].length})`
              : o.value === 'all' && items.length
                ? ` (${items.length})`
                : ''}
          </button>
        ))}
      </div>

      {error && !setupSlug && (
        <div className="alert alert-error">{error}</div>
      )}

      {loading ? (
        <p className="text-secondary">Загрузка…</p>
      ) : (
        <>
          {assignments.length > 0 && (
            <section className="dashboard-section" style={{ marginBottom: '1.5rem' }}>
              <h2>Выдано вам</h2>
              <ul className="practice-quizzes-list">
                {assignments.map((a) => (
                  <li key={a.id} className="practice-quizzes-item">
                    <div className="practice-quizzes-item-main">
                      <h2 className="practice-quizzes-item-title">{a.display_title}</h2>
                      <p className="practice-quizzes-item-meta text-secondary">
                        {a.kind_title || a.kind}
                        {' · '}
                        {a.target_count} заданий
                        {a.has_finished_session ? ' · есть завершённая сессия' : ''}
                      </p>
                    </div>
                    <div className="practice-quizzes-item-actions">
                      {a.latest_session_id && (
                        <Link
                          to={`/trainers/sessions/${a.latest_session_id}`}
                          className="btn btn-outline btn-sm"
                        >
                          Разбор
                        </Link>
                      )}
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={starting}
                        onClick={() => void startAssignment(a.id)}
                      >
                        {starting ? '…' : 'Решать'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {items.length === 0 ? (
            <p className="text-secondary">Пока нет опубликованных тренажёров.</p>
          ) : activeGroup === 'all' ? (
            <div className="practice-quizzes-sections">
              {GROUP_ORDER.filter((g) => (byGroup[g] || []).length > 0).map((group) => {
                const open = !collapsedSections.has(group);
                const count = byGroup[group].length;
                return (
                  <section key={group} className="practice-quizzes-section">
                    <button
                      type="button"
                      className="practice-quizzes-section-toggle"
                      aria-expanded={open}
                      onClick={() => toggleSection(group)}
                    >
                      <span>{GROUP_LABELS[group]}</span>
                      <span className="text-secondary">
                        {count} {pluralTrainers(count)}
                      </span>
                      <span className="practice-quizzes-ege-chevron" aria-hidden>
                        {open ? '▾' : '▸'}
                      </span>
                    </button>
                    {open ? renderGroupBody(group) : null}
                  </section>
                );
              })}
            </div>
          ) : (
            renderGroupBody(activeGroup)
          )}

          {sessions.length > 0 && (
            <section className="dashboard-section" style={{ marginTop: '1.5rem' }}>
              <h2>Недавние сессии</h2>
              <ul className="practice-quizzes-list">
                {sessions.slice(0, 10).map((s) => (
                  <li key={s.id} className="practice-quizzes-item">
                    <div className="practice-quizzes-item-main">
                      <h2 className="practice-quizzes-item-title">
                        {s.kind_title || s.kind}
                      </h2>
                      <p className="practice-quizzes-item-meta text-secondary">
                        {s.problems_correct}/{s.problems_answered} верно
                        {' · '}
                        {s.status === 'finished' ? 'завершена' : 'в процессе'}
                      </p>
                    </div>
                    <div className="practice-quizzes-item-actions">
                      <Link
                        to={`/trainers/sessions/${s.id}`}
                        className="btn btn-outline btn-sm"
                      >
                        Открыть
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <Modal
        open={Boolean(setupSlug)}
        title={meta?.title || setupItem?.title || 'Настройки'}
        onClose={closeSetup}
      >
        {setupItem?.blurb && (
          <p className="text-secondary" style={{ marginBottom: '0.75rem' }}>
            {setupItem.blurb}
          </p>
        )}
        {(error || metaError) && (
          <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>
            {error || metaError}
          </div>
        )}
        {metaLoading ? (
          <p className="text-secondary">Загрузка настроек…</p>
        ) : (
          <TrainerConfigForm meta={meta} config={config} onChange={setConfig} />
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={starting || metaLoading || !isTrainerConfigReady(meta, config)}
            onClick={() => void startFree()}
          >
            {starting ? '…' : 'Начать'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={starting}
            onClick={closeSetup}
          >
            Отмена
          </button>
        </div>
      </Modal>
    </div>
  );
}
